import { Routes, Route, Navigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import ClientProviders from './components/ClientProviders'
import DashboardLayout from './components/DashboardLayout'
import { useAuth } from './hooks/useAuth'

// Page components
import DashboardPage from './pages/Dashboard'
import AppointmentsPage from './pages/Appointments'
import PatientsPage from './pages/Patients'
import PatientSettingsPage from './pages/PatientSettings'
import AppointmentSettingsPage from './pages/appointments/AppointmentSettingsPage'
import RecordsPage from './pages/Records'
import RecordsSettingsPage from './pages/RecordsSettings'
import BillingPage from './pages/Billing'
import SettingsPage from './pages/Settings'
import NotFoundPage from './pages/NotFound'
import ProfilePage from './pages/Profile'
import UserSettingsPage from './pages/UserSettings'
import PracticeSettingsPage from './pages/PracticeSettings'
import PublicBookingPage from './pages/booking/PublicBookingPage'
import LandingV1 from './pages/landing/LandingV1'
import LandingV2 from './pages/landing/LandingV2'
import LandingV3 from './pages/landing/LandingV3'
import LandingV4 from './pages/landing/LandingV4'
import LandingV5 from './pages/landing/LandingV5'
import LandingV6 from './pages/landing/LandingV6'
import LandingV7 from './pages/landing/LandingV7'
import LandingV8 from './pages/landing/LandingV8'
import LandingV9 from './pages/landing/LandingV9'
import LandingV10 from './pages/landing/LandingV10'
import LandingV11 from './pages/landing/LandingV11'
import LandingV12 from './pages/landing/LandingV12'
import LandingV13 from './pages/landing/LandingV13'
import LandingV14 from './pages/landing/LandingV14'
import LandingV15 from './pages/landing/LandingV15'
import LandingV16 from './pages/landing/LandingV16'

function AuthenticatedApp() {
  const { isLoading, isAuthenticated } = useAuth();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // If not authenticated, Django will redirect to login
  // This should not happen if Django is properly protecting routes
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <p className="text-muted-foreground">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      {/* All routes use dashboard layout - user is authenticated */}
      <Route path="/" element={<DashboardLayout />}>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="appointments" element={<AppointmentsPage />} />
        <Route path="appointments/settings" element={<AppointmentSettingsPage />} />
        <Route path="patients" element={<PatientsPage />} />
        <Route path="patients/settings" element={<PatientSettingsPage />} />
        <Route path="records" element={<RecordsPage />} />
        <Route path="records/settings" element={<RecordsSettingsPage />} />
        <Route path="billing" element={<BillingPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="settings/account" element={<UserSettingsPage />} />
        <Route path="practice/settings" element={<PracticeSettingsPage />} />
        <Route path="profile" element={<ProfilePage />} />
      </Route>
      
      {/* 404 fallback */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function App() {
  const path = window.location.pathname;

  if (path === '/1' || path === '/1/') {
    return <LandingV1 />;
  }

  if (path === '/2' || path === '/2/') {
    return <LandingV2 />;
  }

  if (path === '/3' || path === '/3/') {
    return <LandingV3 />;
  }

  if (path === '/4' || path === '/4/') {
    return <LandingV4 />;
  }

  if (path === '/5' || path === '/5/') {
    return <LandingV5 />;
  }

  if (path === '/6' || path === '/6/') {
    return <LandingV6 />;
  }

  if (path === '/7' || path === '/7/') {
    return <LandingV7 />;
  }

  if (path === '/8' || path === '/8/') {
    return <LandingV8 />;
  }

  if (path === '/9' || path === '/9/') {
    return <LandingV9 />;
  }

  if (path === '/10' || path === '/10/') {
    return <LandingV10 />;
  }

  if (path === '/11' || path === '/11/') {
    return <LandingV11 />;
  }

  if (path === '/12' || path === '/12/') {
    return <LandingV12 />;
  }

  if (path === '/13' || path === '/13/') {
    return <LandingV13 />;
  }

  if (path === '/14' || path === '/14/') {
    return <LandingV14 />;
  }

  if (path === '/15' || path === '/15/') {
    return <LandingV15 />;
  }

  if (path === '/16' || path === '/16/') {
    return <LandingV16 />;
  }

  // Detect booking page from Django-injected config
  const isBookingPage =
    (window as any).DJANGO_CONFIG?.config?.isBookingPage === true ||
    path.startsWith('/booking');

  if (isBookingPage) {
    return <PublicBookingPage />;
  }

  return (
    <ClientProviders>
      <Helmet>
        <title>Doctif - Dental Practice Management</title>
        <meta name="description" content="Professional dental practice management system" />
      </Helmet>
      
      <AuthenticatedApp />
    </ClientProviders>
  )
}

export default App

